import { Command } from "cmdk";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Boxes,
  Building2,
  Armchair,
  CalendarDays,
  Database,
  DollarSign,
  LayoutDashboard,
  ListTodo,
  MapPin,
  Search,
  Settings,
  BriefcaseMedical,
  Star,
  Megaphone,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api.ts";
import { keys } from "../../lib/query.ts";
import { useActiveLocation } from "../../lib/use-active-location.ts";

const GROUPS = [
  {
    heading: "Navegación",
    items: [
      { label: "Tablero", to: "/", icon: LayoutDashboard },
      { label: "Turnos", to: "/appointments", icon: CalendarDays },
      { label: "Sala de espera", to: "/waiting-room", icon: Armchair },
      { label: "Pacientes", to: "/patients", icon: Users },
      { label: "Pendientes", to: "/tasks", icon: ListTodo },
      { label: "Tratamientos", to: "/treatments", icon: BriefcaseMedical },
      { label: "Inventario", to: "/inventory", icon: Boxes },
      { label: "Base / CRM", to: "/crm", icon: Database },
      { label: "Finanzas", to: "/finances", icon: DollarSign },
      { label: "Encuestas", to: "/surveys", icon: Star },
      { label: "Marketing", to: "/marketing", icon: Megaphone },
    ],
  },
  {
    heading: "Organización",
    items: [
      {
        label: "Resumen de organización",
        to: "/organization",
        icon: Building2,
      },
      {
        label: "Perfil y horarios",
        to: "/organization/profile",
        icon: Settings,
      },
      {
        label: "Sedes",
        to: "/organization/locations",
        icon: MapPin,
      },
      {
        label: "Profesionales",
        to: "/organization/professionals",
        icon: Users,
      },
      {
        label: "Accesos del equipo",
        to: "/organization/access",
        icon: Settings,
      },
      { label: "Mi perfil", to: "/settings/profile", icon: Users },
      { label: "Seguridad", to: "/settings/security", icon: Settings },
    ],
  },
] as const;

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { id: activeLocationId } = useActiveLocation();
  const triggerRef = useRef<HTMLElement | null>(null);
  const [term, setTerm] = useState("");
  const [patientQuery, setPatientQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setPatientQuery(term.trim()), 200);
    return () => clearTimeout(timer);
  }, [term]);

  // La búsqueda se resuelve en el servidor: nombre, apellido, teléfono y
  // correo. Tratamiento queda fuera porque describe una práctica, no una
  // identidad, y puede devolver cientos de pacientes indistinguibles.
  const { data: patients = [], isFetching: searchingPatients } = useQuery({
    queryKey: keys.patients(patientQuery, activeLocationId),
    queryFn: () =>
      api.patients.list(patientQuery, activeLocationId ?? undefined),
    enabled: open && patientQuery.length >= 2,
  });

  useEffect(() => {
    function shortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (!open) triggerRef.current = document.activeElement as HTMLElement;
        onOpenChange(!open);
      }
    }
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, [onOpenChange, open]);

  function close() {
    onOpenChange(false);
    setTerm("");
    setPatientQuery("");
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      label="Buscar en Ronda"
      className="fixed left-1/2 top-[12vh] z-[101] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
    >
      <div className="flex h-12 items-center gap-3 border-b border-border px-4">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Command.Input
          autoFocus
          placeholder="Buscar pacientes, turnos o secciones…"
          value={term}
          onValueChange={setTerm}
          className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          ESC
        </kbd>
      </div>
      <Command.List className="max-h-[min(60vh,24rem)] overflow-y-auto p-2">
        <Command.Empty className="px-4 py-10 text-center text-sm text-muted-foreground">
          No encontramos ningún destino.
        </Command.Empty>
        {GROUPS.map((group) => (
          <Command.Group
            key={group.heading}
            heading={group.heading}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {group.items.map(({ label, to, icon: Icon }) => (
              <Command.Item
                key={to}
                value={`${label} ${to}`}
                onSelect={() => {
                  close();
                  void navigate({ to });
                }}
                className="flex min-h-10 cursor-default select-none items-center gap-3 rounded-md px-3 text-sm outline-none data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span>{label}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ))}
        {patientQuery.length >= 2 && (
          <Command.Group
            heading="Pacientes"
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {patients.slice(0, 8).map((patient) => (
              <Command.Item
                key={patient.id}
                value={`${patient.firstName} ${patient.lastName} ${patient.phone ?? ""} ${patient.email ?? ""}`}
                onSelect={() => {
                  close();
                  void navigate({
                    to: "/patients/$patientId",
                    params: { patientId: patient.id },
                  });
                }}
                className="flex min-h-10 cursor-default select-none items-center gap-3 rounded-md px-3 text-sm outline-none data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
              >
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {patient.firstName} {patient.lastName}
                  </span>
                  {(patient.phone || patient.email) && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {patient.phone ?? patient.email}
                    </span>
                  )}
                </span>
              </Command.Item>
            ))}
            {searchingPatients && patients.length === 0 && (
              <Command.Loading className="px-3 py-4 text-sm text-muted-foreground">
                Buscando pacientes…
              </Command.Loading>
            )}
          </Command.Group>
        )}
      </Command.List>
      <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
        <span>↑↓ navegar</span>
        <span>↵ abrir</span>
        <span className="ml-auto">Ronda</span>
      </div>
    </Command.Dialog>
  );
}
